import {
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Select,
  SelectItem,
  Space,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { formList, joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, X } from 'tabler-icons-react';
import { cardTypesData } from '../../data/card-types.data.array';
import { CurrenciesData } from '../../data/currencies-data.array';
import { MidFileNames } from '../../enums/mid-file-names.enum';
import { Roles } from '../../enums/roles-enum';
import { removeEmptyPropertiesWithNested } from '../../helpers/filters.helpers';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  AdminCreateMIDsSchema,
  CreateMIDsSchema,
} from '../../schemas/create-mids.schema';
import { ErrorResponse } from '../../type/error-response.types';
import { UpdateFormFile } from '../../type/file.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';
import {
  CreateMIDsRequest,
  CreateMidsResponse,
  FlatCreateMIDsRequest,
} from '../../type/mids.types';
import { BasicSwitches } from '../../type/switch.types';
import UploadButton from '../buttons/file-upload.button/upload.button';
import SelectMerchantFilter from '../filters/select-merchant-filter.component';
import SelectPartnerFilter from '../filters/select-partner-filter.component';
import SelectResellerFilter from '../filters/select-reseller-filter.component';

function AddMids() {
  const [switchOptions, setSwitchOptions] = useState<SelectItem[]>([]);
  const { auth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [switchIsLoading, setSwitchIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [midIdCreated, setMidIdCreated] = useState<number | null>(null);
  const [showPEMError, setShowPEMError] = useState(false);
  const form = useForm<FlatCreateMIDsRequest>({
    initialValues: {
      partnerId: undefined,
      resellerId: undefined,
      merchantId: undefined,

      midName: '',
      switchId: undefined,
      cardTypeId: undefined,
      currencyId: undefined,
      isActive: true,

      xpayMarkup: 0,
      acquirerBuyRate: 0,
      declineRate: 0,
      xpayPerTransactionAddOn: 0,
      clientPerTransactionAddOn: 0,
      fraudControlFee: 0,
      preAuthRate: 0,
      captureRate: 0,
      clientMarkup: 0,
      refundRate: 0,
      voidRate: 0,
      cashBackRate: 0,
      balanceInquiryRate: 0,

      param2: undefined,
      param3: undefined,
      param6: undefined,
      mcc: '',
      pemFile: formList([]),
    },
    validate: joiResolver(
      auth?.role === Roles.ADMINISTRATOR
        ? AdminCreateMIDsSchema
        : CreateMIDsSchema,
    ),
  });

  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      partner: undefined,
      reseller: undefined,
      merchant: undefined,
    },
  });

  useEffect(() => {
    form.setFieldValue('partnerId', filterValues.values.partner);
    form.setFieldValue('resellerId', filterValues.values.reseller);
    form.setFieldValue('merchantId', filterValues.values.merchant);
  }, [filterValues.values]);

  const placeHolder = {
    partner: 'Select a partner',
    reseller: 'Select a reseller',
    merchant: 'Select a merchant',
    midName: 'MID name',
    mcc: 'MCC',

    switch: 'Select Switch',
    cardType: 'Select card type',
    currency: 'Select currency',

    param3: 'Secure Hash',
    param2: 'Merchant ID',
    param6: 'Terminal ID',

    xpayMarkup: 'Xpay mark up',
    acquirerBuyRate: 'Acquirer Buy Rate',
    decline: 'Decline',

    xpayPerTransactionAddOn: 'Per Transaction Add-on (XPAY)',
    clientPerTransactionAddOn: 'Client Per Transaction Add-on',
    fraudControlFee: 'Fraud control fee',
    preAuthRate: 'Pre auth rate',
    captureRate: 'Capture rate',
    finalMerchantRate: 'Final merchant rate',

    clientMarkup: ' Client mark up',
    refundRate: 'Refund rate',
    voidRate: 'Void rate',

    cashBackRate: 'Cash back rate',
    balanceInquiryRate: 'Balance inquiry rate',
  };
  const label = {
    partner: 'Select partner',
    reseller: 'Select reseller',
    merchant: 'Select merchant',
    midName: 'MID name',
    mcc: 'MCC',
    switch: 'Switch',

    cardType: 'Card type',
    currency: 'Currency',

    param3: 'Secure Hash',
    param2: 'Merchant ID',
    param6: 'Terminal ID',

    xpayMarkup: 'Xpay mark up',
    acquirerBuyRate: 'Acquirer Buy Rate',
    decline: 'Decline',

    xpayPerTransactionAddOn: 'Per Transaction Add-on (XPAY)',
    clientPerTransactionAddOn: 'Per Transaction Add-on (Client)',
    fraudControlFee: 'Fraud control fee',
    preAuthRate: 'Pre auth rate',
    captureRate: 'Capture rate',
    finalMerchantRate: 'Final merchant rate',

    clientMarkup: ' Client mark up',
    refundRate: 'Refund rate',
    voidRate: 'Void rate',

    cashBackRate: 'Cash back rate',
    balanceInquiryRate: 'Balance inquiry rate',
    pemFile: 'PEM file',
  };

  useEffect(() => {
    const retrieveSwitches = async () => {
      try {
        setSwitchIsLoading(true);
        const res = await axiosPrivate.get('web/select/switches');
        const basicSwitches: BasicSwitches[] = res.data.items;
        const options: SelectItem[] = basicSwitches.map((item) => {
          return {
            label: item.switchName,
            value: item.switchId.toString(),
          };
        });
        setSwitchOptions(options);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-load-switches',
          message:
            error.error ||
            err?.response?.data ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setSwitchIsLoading(false);
      }
    };

    retrieveSwitches();
  }, []);

  const getFiles = (): File[] => {
    let files: File[] = [];

    if (form.values.pemFile.length > 0) {
      files = form.values.pemFile.map((file) => file.file);
    }

    return files;
  };
  const handleSubmit = async () => {
    // switchId === 6 is Unionbank
    if (form.values.switchId === 6 && form.values.pemFile.length === 0) {
      setShowPEMError(true);
      return;
    }

    const request: CreateMIDsRequest = {
      association: {
        partnerId: form.values.partnerId!,
        resellerId: form.values.resellerId!,
        merchantId: form.values.merchantId!,
      },

      midName: form.values.midName,
      switchId: form.values.switchId!,
      cardTypeId: form.values.cardTypeId!,
      currencyId: form.values.currencyId!,
      isActive: form.values.isActive,

      params: {
        param2: form.values.param2,
        param3: form.values.param3,
        param6: form.values.param6,
        mcc: form.values.mcc,
      },

      rates: {
        xpayMarkup: form.values.xpayMarkup,
        acquirerBuyRate: form.values.acquirerBuyRate,
        declineRate: form.values.declineRate,
        xpayPerTransactionAddOn: form.values.xpayPerTransactionAddOn,
        clientPerTransactionAddOn: form.values.clientPerTransactionAddOn,
        fraudControlFee: form.values.fraudControlFee,
        preAuthRate: form.values.preAuthRate,
        captureRate: form.values.captureRate,
        clientMarkup: form.values.clientMarkup,
        refundRate: form.values.refundRate,
        voidRate: form.values.voidRate,
        cashBackRate: form.values.cashBackRate,
        balanceInquiryRate: form.values.balanceInquiryRate,
      },
    };

    const cleanedRequest = removeEmptyPropertiesWithNested(request);

    try {
      setIsLoading(true);

      let midId = midIdCreated;

      if (midIdCreated === null) {
        const response = await axiosPrivate.post('web/mids', cleanedRequest);
        const createMidResponse: CreateMidsResponse = response.data;
        midId = createMidResponse.midId;
        setMidIdCreated(midId);
      }

      // switchId === 6 is Unionbank
      if (
        midId !== null &&
        form.values.pemFile.length > 0 &&
        form.values.switchId === 6
      ) {
        const formData = new FormData();

        const { pemFile } = form.values;
        const file = pemFile[0].file;

        formData.append(MidFileNames.MID_PEM, new Blob([file]), file.name);

        await axiosPrivate.post(`/web/mids/${midId}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
      }

      showNotification({
        id: 'successfully-created-mids',
        message: 'Successfully created MID',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });

      const { pathname } = location;
      const viewMIDs = pathname.slice(0, pathname.lastIndexOf('/'));
      navigate(viewMIDs);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-mid',
        message:
          error.error || err?.response?.data || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDynamicFilter = () => {
    let filters: JSX.Element = <></>;

    if (auth?.role === Roles.ADMINISTRATOR) {
      filters = (
        <>
          <Grid.Col xs={12} sm={6}>
            <SelectPartnerFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              error={form.errors.partnerId}
            />
          </Grid.Col>
          <Grid.Col p={0} xs={0} sm={6}></Grid.Col>
        </>
      );
    }

    filters = (
      <>
        {filters}
        <Grid.Col xs={12} sm={6}>
          <SelectResellerFilter
            filterSelectData={filterSelectData}
            filterValues={filterValues}
            isPartnerDependent={auth?.role === Roles.ADMINISTRATOR}
            error={form.errors.resellerId}
          />
        </Grid.Col>

        <Grid.Col xs={12} sm={6}>
          <SelectMerchantFilter
            filterSelectData={filterSelectData}
            filterValues={filterValues}
            isResellerDependent={true}
            error={form.errors.merchantId}
          />
        </Grid.Col>
      </>
    );

    return <Grid>{filters}</Grid>;
  };

  return (
    <div style={{ flex: 1, margin: 0 }}>
      <Title order={2} my="lg">
        Create MID
      </Title>

      <Paper
        withBorder
        radius="md"
        shadow="lg"
        style={{
          flex: 1,
          height: '100%',
          padding: '20px 0',
        }}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Container p="lg" fluid>
            {getDynamicFilter()}
            <Grid>
              <Grid.Col xs={12} sm={6}>
                <TextInput
                  label={label.midName}
                  placeholder={placeHolder.midName}
                  {...form.getInputProps('midName')}
                />
              </Grid.Col>

              <Grid.Col
                xs={12}
                sm={6}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <Checkbox
                  label="Is active"
                  {...form.getInputProps('isActive', {
                    type: 'checkbox',
                  })}
                />
              </Grid.Col>

              {/* empty */}

              <Grid.Col xs={12} sm={4}>
                <Select
                  label={label.switch}
                  placeholder={placeHolder.switch}
                  searchable
                  value={
                    switchIsLoading
                      ? 'Loading...'
                      : switchOptions.length === 0
                        ? 'No Data'
                        : form.values.switchId?.toString()
                  }
                  data={
                    switchIsLoading
                      ? ['Loading...']
                      : switchOptions.length === 0
                        ? ['No Data']
                        : switchOptions
                  }
                  onChange={(value) => {
                    form.setFieldValue('switchId', Number(value));
                  }}
                  disabled={switchIsLoading || switchOptions.length === 0}
                  error={form.errors.switchId}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <Select
                  label={label.cardType}
                  placeholder={placeHolder.cardType}
                  searchable
                  data={cardTypesData}
                  value={form.values.cardTypeId?.toString()}
                  onChange={(value) => {
                    form.setFieldValue('cardTypeId', Number(value));
                  }}
                  error={form.errors.cardTypeId}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <Select
                  label={label.currency}
                  placeholder={placeHolder.currency}
                  searchable
                  data={CurrenciesData}
                  value={form.values.currencyId?.toString()}
                  onChange={(value) => {
                    form.setFieldValue('currencyId', Number(value));
                  }}
                  error={form.errors.currencyId}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.param3}
                  placeholder={placeHolder.param3}
                  {...form.getInputProps('param3')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.param2}
                  placeholder={placeHolder.param2}
                  {...form.getInputProps('param2')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.param6}
                  placeholder={placeHolder.param6}
                  {...form.getInputProps('param6')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.mcc}
                  placeholder={placeHolder.mcc}
                  {...form.getInputProps('mcc')}
                />
              </Grid.Col>
            </Grid>

            <Divider my="xl" />

            <Text size="xl" weight={700}>
              Rates
            </Text>
            <Space h="xl" />

            <Grid>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.balanceInquiryRate}
                  placeholder={placeHolder.balanceInquiryRate}
                  {...form.getInputProps('balanceInquiryRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.captureRate}
                  placeholder={placeHolder.captureRate}
                  {...form.getInputProps('captureRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.acquirerBuyRate}
                  placeholder={placeHolder.acquirerBuyRate}
                  {...form.getInputProps('acquirerBuyRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.cashBackRate}
                  placeholder={placeHolder.cashBackRate}
                  {...form.getInputProps('cashBackRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.xpayMarkup}
                  placeholder={placeHolder.xpayMarkup}
                  {...form.getInputProps('xpayMarkup')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.decline}
                  placeholder={placeHolder.decline}
                  {...form.getInputProps('declineRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.xpayPerTransactionAddOn}
                  placeholder={placeHolder.xpayPerTransactionAddOn}
                  {...form.getInputProps('xpayPerTransactionAddOn')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.clientPerTransactionAddOn}
                  placeholder={placeHolder.clientPerTransactionAddOn}
                  {...form.getInputProps('clientPerTransactionAddOn')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.preAuthRate}
                  placeholder={placeHolder.preAuthRate}
                  {...form.getInputProps('preAuthRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.clientMarkup}
                  placeholder={placeHolder.clientMarkup}
                  {...form.getInputProps('clientMarkup')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.refundRate}
                  placeholder={placeHolder.refundRate}
                  {...form.getInputProps('refundRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.voidRate}
                  placeholder={placeHolder.voidRate}
                  {...form.getInputProps('voidRate')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.fraudControlFee}
                  placeholder={placeHolder.fraudControlFee}
                  {...form.getInputProps('fraudControlFee')}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={4}>
                <TextInput
                  label={label.finalMerchantRate}
                  placeholder={placeHolder.finalMerchantRate}
                  disabled
                  value={
                    (
                      Number(form.values.acquirerBuyRate) +
                      Number(form.values.xpayMarkup) +
                      Number(form.values.clientMarkup)
                    ).toFixed(2) +
                    '% + ' +
                    (
                      Number(form.values.xpayPerTransactionAddOn) +
                      Number(form.values.clientPerTransactionAddOn)
                    ).toFixed(2)
                  }
                />
              </Grid.Col>
              {form.values.switchId === 6 ? (
                <Grid.Col>
                  <UploadButton
                    label={label.pemFile}
                    maxNumFiles={1}
                    currentUploadedFiles={getFiles()}
                    addListItem={(droppedFiles: File[]) => {
                      let formattedFiles: UpdateFormFile[] = droppedFiles.map(
                        (file) => {
                          return {
                            id: null,
                            file: file,
                          };
                        },
                      );

                      form.setFieldValue(
                        'pemFile',
                        formList([...formattedFiles]),
                      );

                      setShowPEMError(false);
                    }}
                    removeListItem={(index) => {
                      form.removeListItem('pemFile', index);
                    }}
                  />
                  {showPEMError ? (
                    <Text color="red">Please upload PEM file</Text>
                  ) : (
                    <></>
                  )}
                </Grid.Col>
              ) : (
                <></>
              )}
            </Grid>
            <Divider my="lg" />
            <Group position="apart">
              <Button color="gray">Clear Form</Button>
              <Button type="submit" loading={isLoading}>
                {midIdCreated === null ? 'Submit' : 'Retry File Upload'}
              </Button>
            </Group>
          </Container>
        </form>
      </Paper>
    </div>
  );
}

export default AddMids;
